import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as BackIcon } from "../icons/back.svg";
import "../style/AddProduct.css";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { db, storage } from "../firebase-config";
import {
  collection,
  addDoc,
  doc,
  setDoc,
  query,
  where,
  getDocs,
  getDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

import { ref, uploadString, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";

const animatedComponents = makeAnimated();

const customStyles = {
  container: (provided) => ({
    ...provided,
    maxWidth: "100%",
  }),
  control: (provided) => ({
    ...provided,
    maxWidth: "100%",
    minHeight: "38px",
  }),
  valueContainer: (provided) => ({
    ...provided,
    flexWrap: "wrap",
    overflowY: "auto",
  }),
  multiValue: (provided) => ({
    ...provided,
    maxWidth: "70px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "10px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "black",
    fontSize: "10px",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    cursor: "pointer",
    ":hover": {
      backgroundColor: "#aaa",
      color: "white",
    },
  }),
  menu: (provided) => ({
    ...provided,
    maxHeight: "150px",
    overflowY: "auto",
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: "150px",
    overflowY: "auto",
  }),
};

const AddProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [displayImages, setDisplayImages] = useState([]);
  const [isUpdateValid, setIsUpdateValid] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [productName, setProductName] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState("");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoryError, setCategoryError] = useState(null);
  const productData = location.state?.product || {};
  const [familyName, setFamilyName] = useState("");
  const [brandName, setBrandName] = useState("");
  const [newInToggle, setNewInToggle] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [initialProductData, setInitialProductData] = useState(null);
  const [familyNames, setFamilyNames] = useState([]);
  const [showFamilyNameDropdown, setShowFamilyNameDropdown] = useState(false);
  const [visibility, setVisibility] = useState("All");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Additional section states
  const [additionalContent, setAdditionalContent] = useState([
    { text: "" },
    { text: "" },
    { text: "" },
  ]);

  // Utilisation section state
  const [utilisationContent, setUtilisationContent] = useState("");

  // Composition section state
  const [compositionContent, setCompositionContent] = useState("");

  // D'Autres Information section state
  const [autresInfoContent, setAutresInfoContent] = useState("");

  const handleBackClick = () => {
    if (location.state && location.state.from) {
      navigate(location.state.from, {
        state: { activeComponent: location.state.activeComponent },
      });
    } else {
      navigate("/mc-admin/panel", {
        state: { activeComponent: "productManagement" },
      });
    }
  };

  const handleRemoveDisplayImage = (index) => {
    const newImages = displayImages.filter((_, idx) => idx !== index);
    setDisplayImages(newImages);
  };

  const handleAddDisplayFiles = (event) => {
    const files = event.target.files;
    const newImages = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const reader = new FileReader();
      reader.onload = (e) => {
        newImages.push(e.target.result);
        if (newImages.length === files.length) {
          setDisplayImages((prevImages) => [...prevImages, ...newImages]);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Additional Section handlers
  const handleAdditionalContentChange = (index, value) => {
    const newContent = [...additionalContent];
    newContent[index].text = value;
    setAdditionalContent(newContent);
  };

  const handleAddAdditionalPoint = () => {
    setAdditionalContent([...additionalContent, { text: "" }]);
  };

  const handleRemoveAdditionalPoint = (index) => {
    const newContent = additionalContent.filter((_, idx) => idx !== index);
    setAdditionalContent(newContent);
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const productUUID = initialProductData?.uuid;
      const productID = initialProductData?.productId;

      if (!productUUID && !productID) {
        showNotification("Error: Product UUID or Product ID is missing.");
        setIsLoading(false);
        return;
      }

      // Delete from all categories
      for (const category of initialProductData.categories) {
        const productsSubcollection = collection(
          db,
          "products",
          category.category,
          "items"
        );

        if (productUUID) {
          const querySnapshot = await getDocs(
            query(productsSubcollection, where("uuid", "==", productUUID))
          );

          for (const docSnapshot of querySnapshot.docs) {
            await deleteDoc(docSnapshot.ref);
            console.log(
              `Deleted document ID: ${docSnapshot.id} from category: ${category.category}`
            );
          }
        } else if (productID) {
          const querySnapshot = await getDocs(
            query(productsSubcollection, where("productId", "==", productID))
          );

          for (const docSnapshot of querySnapshot.docs) {
            await deleteDoc(docSnapshot.ref);
            console.log(
              `Deleted document ID: ${docSnapshot.id} from category: ${category.category}`
            );
          }
        }
      }

      showNotification("Product deleted successfully!");
      setTimeout(() => {
        handleBackClick();
      }, 1500);
    } catch (error) {
      console.error("Error deleting product:", error);
      showNotification("Failed to delete product. Please try again.");
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "categories"));
        const categoryList = querySnapshot.docs.map((doc) => ({
          value: doc.id,
          label: doc.id,
        }));

        setCategories(categoryList);

        if (productData?.categories?.length > 0) {
          const visibilities = productData.categories.map((c) => c.visibility);
          const uniqueVisibilities = [...new Set(visibilities)];
          if (uniqueVisibilities.length === 1) {
            setVisibility(uniqueVisibilities[0]);
          } else {
            setVisibility("All");
          }
        }

        if (categoryList.length > 0) {
          setSelectedCategories(
            productData?.categories?.map((cat) => ({
              value: cat.category,
              label: cat.category,
            })) || []
          );
        }
      } catch (error) {
        setCategoryError("Failed to load categories. Please try again.");
        console.error("Error fetching categories:", error);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch existing family names
  useEffect(() => {
    const fetchFamilyNames = async () => {
      try {
        const familyNamesSet = new Set();

        for (const category of categories) {
          const itemsCollection = collection(
            db,
            "products",
            category.value || category,
            "items"
          );
          const querySnapshot = await getDocs(itemsCollection);

          querySnapshot.forEach((doc) => {
            const productData = doc.data();
            if (
              productData.familyName &&
              productData.familyName.trim() !== ""
            ) {
              familyNamesSet.add(productData.familyName);
            }
          });
        }

        setFamilyNames(Array.from(familyNamesSet).sort());
      } catch (error) {
        console.error("Error fetching family names:", error);
      }
    };

    if (categories.length > 0) {
      fetchFamilyNames();
    }
  }, [categories]);

  useEffect(() => {
    if (location.state?.product) {
      const productData = location.state.product;
      console.log("Retrieved product data:", productData);

      setIsEditing(true);
      setInitialProductData(productData);

      setNewInToggle(productData.newIn || false);
      setProductName(productData.name || "");
      setProductPrice(productData.price || "");
      setFamilyName(productData.familyName || "");
      setBrandName(productData.brandName || "");
      setDisplayImages(productData.displayImages || []);

      // Load additional content
      setAdditionalContent(
        productData.additionalContent &&
          productData.additionalContent.length > 0
          ? productData.additionalContent
          : [{ text: "" }, { text: "" }, { text: "" }]
      );

      // Load utilisation content
      setUtilisationContent(productData.utilisationContent || "");

      // Load composition content
      setCompositionContent(productData.compositionContent || "");

      // Load autres info content
      setAutresInfoContent(productData.autresInfoContent || "");
    }
  }, [location.state]);

  useEffect(() => {
    const hasChanges = (current, initial) => {
      return JSON.stringify(current) !== JSON.stringify(initial);
    };

    const isUpdateValid =
      hasChanges(productName, initialProductData?.name) ||
      hasChanges(productPrice, initialProductData?.price) ||
      hasChanges(brandName, initialProductData?.brandName) ||
      hasChanges(displayImages, initialProductData?.displayImages) ||
      hasChanges(selectedCategories, initialProductData?.categories) ||
      hasChanges(visibility, initialProductData?.categories?.[0]?.visibility) ||
      hasChanges(familyName, initialProductData?.familyName) ||
      hasChanges(newInToggle, initialProductData?.newIn) ||
      hasChanges(additionalContent, initialProductData?.additionalContent) ||
      hasChanges(utilisationContent, initialProductData?.utilisationContent) ||
      hasChanges(compositionContent, initialProductData?.compositionContent) ||
      hasChanges(autresInfoContent, initialProductData?.autresInfoContent);

    setIsUpdateValid(isUpdateValid);
  }, [
    productName,
    productPrice,
    brandName,
    displayImages,
    selectedCategories,
    visibility,
    familyName,
    newInToggle,
    additionalContent,
    utilisationContent,
    compositionContent,
    autresInfoContent,
    initialProductData,
  ]);

  useEffect(() => {
    const isFormValid =
      productName.trim() !== "" &&
      productPrice.trim() !== "" &&
      selectedCategories.length > 0 &&
      displayImages.length > 0;

    setIsFormValid(isFormValid);
  }, [productName, productPrice, selectedCategories, displayImages]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isLoading) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isLoading]);

  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => {
      setNotification("");
    }, 3000);
  };

  const checkForUndefinedFields = (data) => {
    Object.entries(data).forEach(([key, value]) => {
      if (value === undefined) {
        console.error(`Field "${key}" is undefined. Setting default value.`);
        data[key] = "";
      } else if (typeof value === "object" && !Array.isArray(value)) {
        checkForUndefinedFields(value);
      }
    });
  };

  const handleUpdate = async () => {
    setIsLoading(true);
    try {
      const productUUID = initialProductData?.uuid;
      const productID = initialProductData?.productId;

      if (!productUUID && !productID) {
        showNotification("Error: Product UUID or Product ID is missing.");
        setIsLoading(false);
        return;
      }

      const updatedProductData = {
        name: productName || "",
        price: productPrice || "",
        familyName: familyName || "",
        brandName: brandName || "",
        newIn: newInToggle || false,
        categories: selectedCategories.map((category) => ({
          category: category.value || "",
          visibility: visibility || "All",
        })),
        additionalContent: additionalContent.filter(
          (item) => item.text.trim() !== ""
        ),
        utilisationContent: utilisationContent.trim(),
        compositionContent: compositionContent.trim(),
        autresInfoContent: autresInfoContent.trim(),
        displayImages: displayImages || [],
      };

      checkForUndefinedFields(updatedProductData);

      for (const selectedCategory of selectedCategories) {
        console.log(`Updating category: ${selectedCategory.value}`);

        const productsSubcollection = collection(
          db,
          "products",
          selectedCategory.value,
          "items"
        );

        if (productUUID) {
          const querySnapshot = await getDocs(
            query(productsSubcollection, where("uuid", "==", productUUID))
          );

          if (!querySnapshot.empty) {
            for (const docSnapshot of querySnapshot.docs) {
              const docId = docSnapshot.id;
              console.log(
                `Updating document ID: ${docId} in category: ${selectedCategory.value}`
              );

              const productRef = doc(
                db,
                "products",
                selectedCategory.value,
                "items",
                docId
              );

              await setDoc(productRef, updatedProductData, { merge: true });
            }
          } else {
            console.error(
              `No matching document found for UUID ${productUUID} in category ${selectedCategory.value}.`
            );
          }
        } else if (productID) {
          const querySnapshot = await getDocs(
            query(productsSubcollection, where("productId", "==", productID))
          );

          if (!querySnapshot.empty) {
            for (const docSnapshot of querySnapshot.docs) {
              const docId = docSnapshot.id;
              console.log(
                `Updating document ID: ${docId} in category: ${selectedCategory.value} using Product ID: ${productID}`
              );

              const productRef = doc(
                db,
                "products",
                selectedCategory.value,
                "items",
                docId
              );

              await setDoc(productRef, updatedProductData, { merge: true });
            }
          } else {
            console.error(
              `No matching document found for Product ID ${productID} in category ${selectedCategory.value}.`
            );
          }
        }
      }

      showNotification("Product updated successfully!");
    } catch (error) {
      console.error("Error updating product:", error);
      showNotification("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async () => {
    setIsLoading(true);
    try {
      const productUUID = selectedCategories.length > 1 ? uuidv4() : null;

      const counterRef = doc(db, "counters", "productCounter");
      const counterSnapshot = await getDoc(counterRef);

      let currentCount = 0;

      if (counterSnapshot.exists()) {
        currentCount = counterSnapshot.data().currentCount;
      } else {
        await setDoc(counterRef, { currentCount: 0 });
      }

      const newCount = currentCount + 1;
      const productID = `MC-${String(newCount).padStart(4, "0")}`;

      const productData = {
        uuid: productUUID,
        productId: productID,
        name: productName,
        price: productPrice,
        familyName: familyName,
        brandName: brandName,
        newIn: newInToggle,
        categories: selectedCategories.map((category) => ({
          category: category.value,
          visibility: visibility || "All",
        })),
        additionalContent: additionalContent.filter(
          (item) => item.text.trim() !== ""
        ),
        utilisationContent: utilisationContent.trim(),
        compositionContent: compositionContent.trim(),
        autresInfoContent: autresInfoContent.trim(),
        displayImages: [],
        createdAt: serverTimestamp(),
      };

      const uploadImage = async (image, path) => {
        const storageRef = ref(storage, path);
        const uploadTask = await uploadString(storageRef, image, "data_url");
        return getDownloadURL(uploadTask.ref);
      };

      const displayImageUrls = await Promise.all(
        displayImages.map((image, index) =>
          uploadImage(
            image,
            `products/${productName.replace(/\s+/g, "-")}/display/${index}`
          )
        )
      );

      productData.displayImages = displayImageUrls;

      for (const selectedCategory of selectedCategories) {
        const productsCollection = collection(db, "products");
        const categoryDoc = doc(productsCollection, selectedCategory.value);

        await setDoc(categoryDoc, {}, { merge: true });
        const productsSubcollection = collection(categoryDoc, "items");

        await addDoc(productsSubcollection, productData);
      }

      await setDoc(counterRef, { currentCount: newCount });

      showNotification("Product uploaded successfully!");

      // Reset form fields
      setProductName("");
      setProductPrice("");
      setSelectedCategories([]);
      setVisibility("All");
      setBrandName("");
      setAdditionalContent([{ text: "" }, { text: "" }, { text: "" }]);
      setUtilisationContent("");
      setCompositionContent("");
      setAutresInfoContent("");
      setDisplayImages([]);
      setNewInToggle(false);
      setFamilyName("");
    } catch (error) {
      console.error("Error uploading product:", error);
      showNotification("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="add-product-container-unique">
      <div className="add-product-header-unique">
        <div className="back-and-title-container">
          <div className="back-button-unique" onClick={handleBackClick}>
            <BackIcon className="back-icon-unique" />
            <span>Back</span>
          </div>
          <span className="add-product-title-unique">
            {isEditing ? "Edit Product" : "Add Product"}
          </span>
        </div>
        <div className="upload-button-container-unique">
          {!isEditing && (
            <div
              className={`upload-button-unique ${
                !isFormValid || isLoading ? "disabled" : ""
              }`}
              onClick={isFormValid && !isLoading ? handleUpload : null}
            >
              {isLoading ? <span className="loader"></span> : "Upload Product"}
            </div>
          )}

          {isEditing && (
            <>
              <div
                className="upload-button-unique"
                style={{
                  backgroundColor: "#ff4444",
                  marginRight: "10px",
                }}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete Product
              </div>
              <div
                className={`upload-button-unique ${
                  !isUpdateValid || isLoading ? "disabled" : ""
                }`}
                onClick={isUpdateValid && !isLoading ? handleUpdate : null}
              >
                {isLoading ? (
                  <span className="loader"></span>
                ) : (
                  "Update Product"
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <div className="content-container-unique">
        <div className="split-section-unique small-section-unique">
          <div className="new-product-section">
            <div className="new-section-title">Product</div>
            <div className="new-product-container">
              <label className="new-form-label" htmlFor="productName">
                Name
              </label>
              <input
                className="new-form-input"
                type="text"
                id="productName"
                name="productName"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
              />
              <div className="new-form-row">
                <div className="new-form-group">
                  <label className="new-form-label" htmlFor="productPrice">
                    Price ($)
                  </label>
                  <input
                    className="new-form-input-small"
                    type="number"
                    id="productPrice"
                    name="productPrice"
                    value={productPrice}
                    onChange={(e) => setProductPrice(e.target.value)}
                  />
                </div>
                <div className="new-form-group">
                  <label className="new-form-label" htmlFor="familyName">
                    Family Name
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      className="new-form-input"
                      type="text"
                      id="familyName"
                      name="familyName"
                      value={familyName}
                      onChange={(e) => {
                        setFamilyName(e.target.value);
                        setShowFamilyNameDropdown(e.target.value.length > 0);
                      }}
                      onFocus={() =>
                        setShowFamilyNameDropdown(
                          familyName.length > 0 || familyNames.length > 0
                        )
                      }
                      onBlur={() =>
                        setTimeout(() => setShowFamilyNameDropdown(false), 200)
                      }
                      placeholder="Family name"
                    />
                    {showFamilyNameDropdown && familyNames.length > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          maxHeight: "200px",
                          overflowY: "auto",
                          zIndex: 1000,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                      >
                        {familyNames
                          .filter((name) =>
                            name
                              .toLowerCase()
                              .includes(familyName.toLowerCase())
                          )
                          .map((name, index) => (
                            <div
                              key={index}
                              onClick={() => {
                                setFamilyName(name);
                                setShowFamilyNameDropdown(false);
                              }}
                              style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                borderBottom: "1px solid #f0f0f0",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor = "#f5f5f5")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor = "white")
                              }
                            >
                              {name}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <label className="new-form-label" htmlFor="category">
                Category
              </label>
              {loadingCategories ? (
                <div>Loading categories...</div>
              ) : categoryError ? (
                <div>{categoryError}</div>
              ) : (
                <Select
                  options={categories}
                  value={selectedCategories[0] || null}
                  onChange={(selectedOption) =>
                    setSelectedCategories(
                      selectedOption ? [selectedOption] : []
                    )
                  }
                  components={animatedComponents}
                  styles={customStyles}
                />
              )}

              <div className="visibility-control-unique">
                <label className="visibility-label-unique">Visibility</label>
                <select
                  className="visibility-select-unique"
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value)}
                >
                  <option value="All">All</option>
                  <option value="No">No</option>
                  <option value="Limited">Limited</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="split-section-unique medium-section-unique">
          <div className="customize-section">
            <div className="customize-title">Brand</div>
            <div className="customize-container">
              <label className="new-form-label" htmlFor="brandName">
                Brand Name
              </label>
              <input
                className="new-form-input"
                type="text"
                id="brandName"
                name="brandName"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Enter brand name"
              />
            </div>
          </div>

          {/* Conçu Pour Section */}
          <div className="customize-section" style={{ marginTop: "20px" }}>
            <div className="customize-title">
              Conçu Pour (Additional Section)
            </div>
            <div className="customize-container">
              <label className="new-form-label" style={{ marginTop: "15px" }}>
                Content Points
              </label>
              {additionalContent.map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    gap: "10px",
                    marginBottom: "10px",
                    alignItems: "center",
                  }}
                >
                  <textarea
                    className="new-form-input"
                    value={item.text}
                    onChange={(e) =>
                      handleAdditionalContentChange(index, e.target.value)
                    }
                    placeholder={`Point ${index + 1}`}
                    rows="2"
                    style={{ flex: 1 }}
                  />
                  {additionalContent.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveAdditionalPoint(index)}
                      style={{
                        backgroundColor: "#ff4444",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontSize: "18px",
                        lineHeight: "1",
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddAdditionalPoint}
                style={{
                  backgroundColor: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  padding: "10px 20px",
                  cursor: "pointer",
                  marginTop: "10px",
                }}
              >
                Add Point
              </button>
            </div>
          </div>
        </div>

        <div className="split-section-unique large-section-unique">
          <div className="split-title-unique">Product Images</div>
          <div className="product-images-inner-container-unique">
            <div className="image-section-unique">
              <div className="image-section-title-unique">On Display</div>
              <label className="add-files-link-unique">
                Click Here to add files
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleAddDisplayFiles}
                />
              </label>

              <div className="images-container-unique">
                {displayImages.map((image, index) => (
                  <div key={index} className="image-container-unique">
                    <img
                      src={image}
                      alt={`Display ${index}`}
                      className="image-fit"
                    />
                    <div
                      className="cancel-icon"
                      onClick={() => handleRemoveDisplayImage(index)}
                    >
                      ×
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="toggle-container">
            <input
              type="checkbox"
              id="newInToggle"
              className="toggle-switch"
              checked={newInToggle}
              onChange={(e) => setNewInToggle(e.target.checked)}
            />
            <label htmlFor="newInToggle" className="toggle-label">
              New in
            </label>
          </div>
        </div>
      </div>

      {/* Horizontal Container for Three Sections - FULL WIDTH */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "20px",
          marginTop: "20px",
          padding: "0 20px",
        }}
      >
        {/* Utilisation Section */}
        <div className="customize-section">
          <div className="customize-title">Utilisation (Usage)</div>
          <div className="customize-container">
            <textarea
              className="new-form-input"
              value={utilisationContent}
              onChange={(e) => setUtilisationContent(e.target.value)}
              placeholder="Enter usage information"
              rows="6"
              style={{ width: "100%", resize: "vertical" }}
            />
          </div>
        </div>

        {/* Composition Section */}
        <div className="customize-section">
          <div className="customize-title">Composition</div>
          <div className="customize-container">
            <textarea
              className="new-form-input"
              value={compositionContent}
              onChange={(e) => setCompositionContent(e.target.value)}
              placeholder="Enter composition information"
              rows="6"
              style={{ width: "100%", resize: "vertical" }}
            />
          </div>
        </div>

        {/* D'Autres Information Section */}
        <div className="customize-section">
          <div className="customize-title">
            D'Autres Information (Other Information)
          </div>
          <div className="customize-container">
            <textarea
              className="new-form-input"
              value={autresInfoContent}
              onChange={(e) => setAutresInfoContent(e.target.value)}
              placeholder="Enter other information"
              rows="6"
              style={{ width: "100%", resize: "vertical" }}
            />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "8px",
              maxWidth: "400px",
              textAlign: "center",
            }}
          >
            <h3 style={{ marginBottom: "20px" }}>Delete Product?</h3>
            <p style={{ marginBottom: "30px", color: "#666" }}>
              Are you sure you want to delete "{productName}"? This action
              cannot be undone.
            </p>
            <div
              style={{ display: "flex", gap: "10px", justifyContent: "center" }}
            >
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#ccc",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isLoading}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#ff4444",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: isLoading ? "not-allowed" : "pointer",
                }}
              >
                {isLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {notification && (
        <div className={`notification ${notification ? "show" : ""}`}>
          {notification}
        </div>
      )}
    </div>
  );
};

export default AddProduct;
